import { Button, Col, Form, Row } from 'react-bootstrap';
import authApi from '@/app/common/api/authApi';
import { isEmpty } from 'lodash';
import { Formik } from 'formik';
import { array, object, string } from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCreateDomainMutation } from '@/app/Domain/api/domainsApi';
import { errorMessage, successMessage } from '@/utils/toast';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AddDomainFormValues } from './types';

interface AddDomainDrawerContentProps {
  maxCompetitors: number;
  setCompetitorsCount: (count: number) => void;
  competitorsCount: number;
  isMemberStep?: boolean;
  skipKeywordsStep?: boolean;
  setShow?: (show: boolean) => void;
}

const AddDomainDrawerContent = ({
  maxCompetitors,
  setCompetitorsCount,
  competitorsCount,
  isMemberStep = false,
  skipKeywordsStep = false,
  setShow = () => {},
}: AddDomainDrawerContentProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createDomain] = useCreateDomainMutation();
  const showCompetitors = !isMemberStep || !skipKeywordsStep;

  return (
    <Formik<AddDomainFormValues>
      validationSchema={object().shape({
        url: string()
          .trim()
          .required('وارد کردن دامنه الزامی می‌باشد')
          .test(
            'is-not-http-or-www',
            'دامنه نباید با http، https یا www شروع شود',
            value => {
              if (!value || value.length < 3) return true;
              return /^(?!https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/.test(value);
            },
          )
          .test(
            'contains-dot',
            'دامنه نامعتبر است. لطفا یک دامنه معتبر وارد کنید.',
            value => (value ? value.includes('.') : true),
          )
          .test(
            'no-slash',
            'لطفا "/" و هرآنچه بعد از آن را پاک کنید. ',
            value => !value.includes('/'),
          )
          .max(50, 'دامنه شما نمیتواند بیشتر از ۵۰ کاراکتر باشد.'),
        show_name: string().max(20, 'عنوان نباید بیش از ۲۰ کاراکتر باشد'),
        competitors: array().of(
          object().shape({
            url: string()
              .trim()
              .required('وارد کردن دامنه رقیب الزامی می‌باشد')
              .test(
                'is-not-http-or-www',
                'دامنه نباید با http، https یا www شروع شود',
                value => {
                  if (!value || value.length < 3) return true;
                  return /^(?!https?:\/\/|www\.)[^\s/$.?#].[^\s]*$/.test(value);
                },
              )
              .test(
                'contains-dot',
                'دامنه نامعتبر است. لطفا یک دامنه معتبر وارد کنید.',
                value => (value ? value.includes('.') : true),
              )
              .test(
                'no-slash',
                'لطفا "/" و هرآنچه بعد از آن را پاک کنید. ',
                value => !value.includes('/'),
              )
              .max(50, 'دامنه رقیب نمیتواند بیش از ۵۰ کاراکتر باشد.'),
            show_name: string().max(20, 'عنوان نباید بیش از ۲۰ کاراکتر باشد'),
          }),
        ),
      })}
      onSubmit={async ({ url, show_name, competitors }) => {
        createDomain({
          competitors: competitors
            .filter(competitor => competitor && !isEmpty(competitor))
            .map(c => ({
              url: c.url?.trim()?.toLowerCase(),
              show_name: c.show_name?.trim() || '',
            })),
          url: url.trim()?.toLowerCase(),
          show_name: show_name.trim(),
        })
          .unwrap()
          .then(res => {
            if (res?.success) successMessage('دامنه با موفقیت اضافه شد');
            dispatch(authApi.util.invalidateTags(['UserInfo']));

            if (isMemberStep) {
              navigate(
                skipKeywordsStep
                  ? '/'
                  : `/membership-steps/keywords/${res?.data?.domain_slug}`,
              );
            } else {
              setShow(false);
            }
            setCompetitorsCount(0);
          })
          .catch(err => {
            errorMessage('خطایی در ایجاد دامنه وجود داره');
            console.error(err);
          });
      }}
      initialValues={{ url: '', show_name: '', competitors: [] }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldTouched,
        setFieldValue,
      }) => (
        <Form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Row className="g-3 mt-4">
            <Form.Group as={Col} md={7}>
              <Form.Label>دامنه خودت</Form.Label>
              <Form.Control
                onBlur={handleBlur}
                onChange={handleChange}
                style={{ direction: 'ltr' }}
                id="url"
                name="url"
                value={values.url}
                isInvalid={Boolean(touched.url && errors.url)}
                isValid={Boolean(touched.url && !errors.url)}
                placeholder="mizfa.com"
              />
              <Form.Control.Feedback
                type="invalid"
                className="fs--1 ms-2 mt-2 d-block"
              >
                {errors.url && touched.url && errors.url}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>نام نمایشی</Form.Label>
              <Form.Control
                isInvalid={Boolean(touched.show_name && errors.show_name)}
                isValid={Boolean(touched.show_name && !errors.show_name)}
                onBlur={handleBlur}
                onChange={handleChange}
                id="show_name"
                name="show_name"
                value={values.show_name}
                placeholder="میزفاتولز"
              />
              <Form.Control.Feedback
                type="invalid"
                className="fs--1 ms-2 mt-2 d-block"
              >
                {errors.show_name && touched.show_name && errors.show_name}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <div className="fs--1 ms-2 mt-1 mb-3 text-secondary">
            لطفا دامنه رو با "www"، "http" و "https" وارد نکنید، مانند:{' '}
            <strong>mizfa.com</strong>
          </div>
          {showCompetitors &&
            Array.from({ length: competitorsCount }, (_, index) => index).map(
              c => (
                <Row className="g-2 mb-3" key={c}>
                  <Col md={7}>
                    <div className="d-flex fs--1 flex-column">
                      <Form.Group>
                        <Form.Control
                          onBlur={() =>
                            setFieldTouched(`competitors[${c}].url`, true)
                          }
                          onChange={e => {
                            const competitorsCopy = [...values.competitors];
                            competitorsCopy[c] = {
                              ...competitorsCopy[c],
                              url: e.target.value,
                            };
                            setFieldValue('competitors', competitorsCopy);
                          }}
                          style={{ direction: 'ltr' }}
                          id={`competitor-url-${c}`}
                          name={`competitors[${c}].url`}
                          value={values.competitors[c]?.url || ''}
                          placeholder="mizfa.com"
                        />
                        <Form.Control.Feedback
                          type="invalid"
                          className="fs--1 ms-2 mt-2 d-block"
                        >
                          {errors.competitors &&
                            touched.competitors &&
                            touched.competitors[c] &&
                            errors.competitors[c]?.url}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </Col>
                  <Col md={5}>
                    <div className="d-flex fs--1 flex-column">
                      <div className="d-flex gap-2">
                        <Form.Control
                          onChange={e => {
                            const competitorsCopy = [...values.competitors];
                            competitorsCopy[c] = {
                              ...competitorsCopy[c],
                              show_name: e.target.value,
                            };
                            setFieldValue('competitors', competitorsCopy);
                          }}
                          id={`competitor-show_name-${c}`}
                          name={`competitors[${c}].show_name`}
                          value={values.competitors[c]?.show_name || ''}
                          placeholder="عنوان رقیب"
                        />
                        <Button
                          variant="danger"
                          onClick={() => {
                            const competitorsCopy = [...values.competitors];
                            competitorsCopy.splice(c, 1);
                            setCompetitorsCount(competitorsCount - 1);
                            setFieldValue('competitors', competitorsCopy);
                          }}
                          style={{ padding: '9.5px', height: 36 }}
                          className="justify-content-center gap-1 border-300 d-flex align-items-center"
                        >
                          <FontAwesomeIcon icon="trash-alt" className="fs-0" />{' '}
                        </Button>
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className="fs--1 ms-2 mt-2 d-block"
                      >
                        {errors.competitors &&
                          errors.competitors[c] &&
                          errors.competitors[c].show_name}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                </Row>
              ),
            )}
          {showCompetitors && (
            <Row className="g-0 my-3 p-0 justify-content-between">
              <Col sm="auto">
                <ChildrensTooltip
                  html={
                    values.competitors.length >= maxCompetitors
                      ? 'تعداد رقبا با طرح خریداری شده مطابقت ندارد.'
                      : ''
                  }
                  placement="top"
                >
                  <Button
                    disabled={
                      (values.competitors.length > 0 &&
                        !values.competitors[values.competitors.length - 1]
                          .url) ||
                      values.competitors.length >= maxCompetitors
                    }
                    className="d-flex gap-2 align-items-center border-0"
                    onClick={() => {
                      setCompetitorsCount(competitorsCount + 1);
                      setFieldValue('competitors', [
                        ...values.competitors,
                        { show_name: '', url: '' },
                      ]);
                    }}
                    variant="falcon-primary"
                  >
                    <FontAwesomeIcon size="sm" icon="plus" />
                    افزودن رقیب
                  </Button>
                </ChildrensTooltip>
              </Col>
            </Row>
          )}
          <div className="position-absolute bottom-0 d-flex gap-2 end-0 p-4">
            {!isMemberStep && (
              <Button
                className="d-flex gap-2 align-items-center"
                variant="outline-secondary"
                onClick={() => {
                  setShow(false);
                  setCompetitorsCount(0);
                }}
              >
                <FontAwesomeIcon size="lg" icon="times" />
                لغو
              </Button>
            )}
            <Button
              disabled={values.url.length === 0}
              className="d-flex gap-2 align-items-center border-0"
              type="submit"
              variant="falcon-primary"
            >
              <FontAwesomeIcon size="lg" icon="plus" />
              افزودن دامنه
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddDomainDrawerContent;
